/** @jsx jsx */
import { jsx } from '@emotion/core';

import React, { Fragment, useState, useEffect } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Button, Divider, FormHelperText, Grid, useTheme } from '@material-ui/core';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
import Modal from '../../../../pge-plus-common/Modal';
import colors from '../../../../../themes/main-colors';
import routes from '../../../../../routes';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import useContentMgmt, { LinkedDropdownData } from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import { ChargerType, SmartCharging } from '../rebateOnlyForm.types';
import { uploadModelNumberValidateFunction } from '../rebateOnlyForm.rules';
import { getExistingChargerEnrollment } from '../rebateOnlyForm.utils';
import useFormState from '../../../../../hooks/useFormState';
import FileUploaderWithOCR from '../../../../pge-plus-common/FileUploaderWithOCR';
import { Question } from '../../../../pge-plus-common/Typography';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import TextField from '../../../../text-field';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import { rebateInfoERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import {
  ApplicationErSectionType,
  OcrFieldNames,
  SectionErRebateInfo,
} from '../../../../../__generated__/pgeplus-types';
import WelcomeToPGEPlusModal from '../../../../pge-plus-common/WelcomeToPGEPlusModal';
import { DoneRounded } from '@material-ui/icons';
import { DropdownMenu } from '../../../../pge-plus-common/DropdownMenu';
import linkedDropdownGroupIds from '../../../../pge-plus-common/linkedDropdownGroupIds';
import { haveChargerOrVehicleInputsChanged } from '../../../../pge-plus-common/utils/chargerVehicleQualification';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    imageTitle: {
      fontSize: '16px !important',
      padding: '0px !important',
      color: `${colors.sparkBlue} !important`,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: 'unset !important',
      },
    },
    errorMessage: {
      fontFamily: 'Untitled-Sans',
      fontSize: '1rem !important',
      letterSpacing: '1px',
    },
    informationBox: {
      borderRadius: 5,
      margin: '20px 0 20px 0',
      padding: 20,
      background: 'white',
    },
    informationItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
    },
    informationCheck: {
      marginRight: '10px',
      color: colors.shoreGreen,
    },
  }),
);

const UploadModelNumber: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CONFIRM_YOUR_CHARGER,
  );
  const { content: contentMCG, getLinkedDropdown } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG,
  );

  const [linkedDropdown, setLinkedDropdown] = useState<LinkedDropdownData>({
    parents: [],
    childrenByParent: new Map(),
  });

  useEffect(() => {
    const fetchLinkedDropdown = async () => {
      const data = await getLinkedDropdown({
        linkedDropdownGroupEntryId:
          linkedDropdownGroupIds.PGE_PLUS_LINKED_DROPDOWN_GROUP_VEHICLE_OPTIONS,
      });
      setLinkedDropdown(data);
    };
    void fetchLinkedDropdown();
  }, []);

  const { parents: vehicleManufacturers, childrenByParent: vehicleModels } = linkedDropdown;

  const theme = useTheme();
  const classes = useStyles(props);
  const { wrapWithLoader } = useWrapWithLoader();
  const { content: fileUploaderText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS,
  );

  const [openExamplePhotoModal, setOpenExamplePhotoModal] = useState<boolean>(
    false,
  );
  const [ocrExtractionStatus, setOcrExtractionStatus] = useState<
    string | undefined
  >();
  const {
    formState: { rebateInfo, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();
  const form = useFormState(
    rebateInfo.smartCharging!, //TODO: This should be taken from the global app state
    {
      validate: uploadModelNumberValidateFunction(),
      validationContext: {
        content,
        params: { vehicleModels },
      },
    },
  );

  const vehicleModelOptions = vehicleModels.get(form.values.vehicleManufacturer || '') || [];

  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  };

  const boldText = {
    fontWeight: 700,
  };

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateInfo({
        ...rebateInfoERFormStateAdapter().fromFormState(
          {
            ...rebateInfo,
            smartCharging: form.values,
          },
          application?.id!,
          sectionDataRebateInfo?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateInfo?.validation?.errors ||
        response.data?.updateSectionERRebateInfo?.section?.errors.length !== 0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  const sectionDataRebateInfo: SectionErRebateInfo = getSectionData(
    ApplicationErSectionType.ErRebateInfo,
  );

  const existingEnrollments =
    sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments;
  const existingChargerEnrollment = getExistingChargerEnrollment(
    existingEnrollments,
  );

  const backRoute = () => {
    if (existingEnrollments?.evPulse?.length) {
      return routes.PGE_PLUS_REBATE_ONLY_VERIFY_EVPULSE_REBATE;
    } else if (existingChargerEnrollment) {
      return routes.PGE_PLUS_REBATE_ONLY_VERIFY_CHARGER_REBATE;
    } else if (sectionDataRebateInfo?.data?.mustVerifyResidentialMeter) {
      return routes.PGE_PLUS_REBATE_ONLY_CONFIRM_YOUR_INFORMATION;
    } else {
      return routes.PGE_PLUS_REBATE_ONLY_HOME;
    }
  };

  const { updateSectionRebateInfo } = useUpdateERSections();

  const { setErrorNotification } = useUtil();

  const onSubmit = wrapWithLoader(async (data: SmartCharging) => {
    try {
      const response = await updateSectionRebateInfo(
        rebateInfoERFormStateAdapter().fromFormState(
          {
            ...rebateInfo,
            smartCharging: data,
            enrollmentPreference: haveChargerOrVehicleInputsChanged(
              data,
              rebateInfo.smartCharging,
            )
              ? undefined
              : rebateInfo.enrollmentPreference,
            smartChargingEnrollmentConfirmation: existingChargerEnrollment
              ? rebateInfo.smartChargingEnrollmentConfirmation
              : undefined,
            evPulseEnrollmentConfirmation: sectionDataRebateInfo?.data
              .rebateInfo?.existingEnrollments?.evPulse?.length
              ? rebateInfo.evPulseEnrollmentConfirmation
              : undefined,
          },
          application?.id!,
          sectionDataRebateInfo?.id,
        ),
      );
      // Save the response in a local var
      const result = response?.data?.updateSectionERRebateInfo;

      // Check for errors to show the red banner and block navigation
      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      //Update the app state with the user selected values
      if (result?.section.data) {
        const updatedValues = rebateInfoERFormStateAdapter().toFormState(
          result?.section.data,
        );
        updateFormState('rebateInfo', {
          ...updatedValues,
        });
      }
      if (result?.section?.errors.length === 0) {
        const chargerIsQualified = !!result?.section?.data?.chargerValidation
          ?.isQualifiedProduct;
        const vehicleIsQualified = !!result?.section?.data?.vehicleValidation
          ?.isQualifiedVehicle;

        if (!chargerIsQualified && !vehicleIsQualified) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
            state: {
              title: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CHARGER_AND_VEHICLE',
              message: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CHARGER_AND_VEHICLE_MESSAGE',
              message2: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CHARGER_AND_VEHICLE_MESSAGE2',
              showBackButton: true,
              backButtonText: 'REUSABLE_BUTTON_BACK',
              showNextButton: true,
              nextButtonText: 'RETURN_TO_MY_ACCOUNT',
              nextButtonRoute: routes.ACCOUNT,
            },
          });
          return;
        }
        if (chargerIsQualified && !vehicleIsQualified) {
          const isJuiceBox =
            result?.section?.data?.chargerValidation?.product?.brand ===
            ChargerType.JUICE_BOX;
          void navigate(
            isJuiceBox
              ? routes.PGE_PLUS_REBATE_ONLY_UPLOAD_JUICEBOXID_NUMBER
              : routes.PGE_PLUS_REBATE_ONLY_UPLOAD_SERIAL_NUMBER,
          );
          return;
        }
        if (!chargerIsQualified && vehicleIsQualified) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN);
          return;
        }
        if (chargerIsQualified && vehicleIsQualified) {
          void navigate(
            routes.PGE_PLUS_REBATE_ONLY_ENROLL_WITH_CHARGER_OR_VEHICLE,
          );
          return;
        }
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <form
      onSubmit={form.submit(values => onSubmit(values))}
      noValidate
    >
      <Box>
        <Typography css={{ ...subText1, ...boldText }}>
          {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_READY_TO_START_YOUR_APPLICATION')}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body1">
          {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_WE_NEED_SOME_INFORMATION')}
        </Typography>
      </Box>
      <Box display={'flex'} justifyContent={'center'}>
        <Grid container className={classes.informationBox} xs={12}>
          <Grid item xs={12}>
            <Typography
              variant={'body1'}
              css={{ fontWeight: 700, fontSize: 20, marginBottom: 15 }}
            >
              {contentMCG.get(
                'PGE_PLUS_EV_REBATES_FLOW_THE_INFORMATION_WE_NEED',
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.informationItem}>
              <DoneRounded className={classes.informationCheck} />
              <Typography variant="body1">
                {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_INFORMATION_ITEM_01')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.informationItem}>
              <DoneRounded className={classes.informationCheck} />
              <Typography variant="body1">
                {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_INFORMATION_ITEM_02')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.informationItem}>
              <DoneRounded className={classes.informationCheck} />
              <Typography variant="body1">
                {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_INFORMATION_ITEM_03')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.informationItem}>
              <DoneRounded className={classes.informationCheck} />
              <Typography variant="body1">
                {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_INFORMATION_ITEM_04')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* Instruction 1: UPLOAD_PHOTO_CHARGER_MODEL_NUMBER */}
      <Box>
        <Typography css={{ ...subText1, ...boldText }}>
          {contentMCG.get(
            'PGE_PLUS_EV_REBATES_FLOW_UPLOAD_PHOTO_CHARGER_MODEL_NUMBER',
          )}
        </Typography>
      </Box>
      <Box>
        <PopoverToolTip
          baseContent={content.get('HOW_TO_FIND_YOUR_CHARGER_MODEL_NUMBER')}
          popoverContent={richText(
            content.get('HOW_TO_FIND_MODEL_NUMBER_TOOLTIP'),
          )}
        />
      </Box>
      <Box mt={2}>
        <Fragment>
          {!form.values?.modelNumberImage && (
            <Box mt={3}>
              <Button
                className={classes.imageTitle}
                onClick={() => setOpenExamplePhotoModal(true)}
              >
                {content.get('REUSABLE_VIEW_EXAMPLE_PHOTO')}
              </Button>
              <Modal
                open={openExamplePhotoModal}
                onClose={() => setOpenExamplePhotoModal(false)}
                title={content.get('MODEL_SAMPLE_PHOTO_TITLE')}
                showCloseIcon={true}
                modalStyles={{ width: 600 }}
              >
                {' '}
                <Box mt={3}>
                  <img
                    css={{
                      width: '100%',
                      maxHeight: '350px',
                      objectFit: 'cover',
                    }}
                    src={content.get('MODEL_SAMPLE_PHOTO_URL')}
                  />
                </Box>
              </Modal>
            </Box>
          )}
          <FileUploaderWithOCR
            placeholder={fileUploaderText.get('UPLOAD_A_PHOTO')}
            reuploadLabel={fileUploaderText.get('REUPLOAD_PHOTO')!}
            ocrType={OcrFieldNames.ModelNumber}
            onComplete={(key, extractedText, message, status) => {
              void form.setValue('modelNumberImage', { id: key });
              void form.setValue('modelNumber', extractedText);
              setOcrExtractionStatus(status);
            }}
            image={form.values?.modelNumberImage}
          />
          {form.errors?.modelNumberImage && (
            <FormHelperText className={classes.errorMessage} error>
              {form.errors?.modelNumberImage}
            </FormHelperText>
          )}

          {form.values?.modelNumberImage && (
            <Box
              css={{
                marginBottom: theme.spacing(1),
                '& .MuiFormControl-root': {
                  background: 'transparent',
                },
                '& .MuiInputBase-input': {
                  background: '#FFF',
                },
              }}
            >
              {ocrExtractionStatus && ocrExtractionStatus === 'fail' && (
                <FormHelperText
                  className={classes.errorMessage}
                  error
                  component="div"
                >
                  {fileUploaderText.get('OCR_FAIL_MESSAGE')}
                </FormHelperText>
              )}
              <Typography
                css={{
                  ...subText1,
                  ...boldText,
                  marginTop: theme.spacing(3),
                  marginBottom: theme.spacing(1),
                }}
              >
                {ocrExtractionStatus && ocrExtractionStatus === 'success'
                  ? content.get('REBATE_ONLY_EDIT_MODEL_HEADER')
                  : content.get('MODEL_NUMBER')}
              </Typography>
              <TextField
                css={{
                  background: 'transparent',
                  '.MuiInputBase-formControl': {
                    background: colors.white,
                  },
                  width: '100%',
                }}
                name={'modelNumber'}
                {...form.props('modelNumber')}
              />

              {ocrExtractionStatus && ocrExtractionStatus === 'success' && (
                <Typography css={{ marginTop: 8 }}>
                  {content.get('MODEL_OCR_SUCCESS_MESSAGE')}
                </Typography>
              )}
            </Box>
          )}
        </Fragment>
      </Box>
      {/* Instruction 2: SELECT_VEHICLE_MANUFACTURER */}
      <Box mt={4}>
        <Typography css={{ ...subText1, ...boldText }}>
          {contentMCG.get(
            'PGE_PLUS_EV_REBATES_FLOW_SELECT_VEHICLE_MANUFACTURER',
          )}
        </Typography>
      </Box>

      <Box>
        <Grid item xs={12}>
          <DropdownMenu
            name={contentMCG.get(
              'PGE_PLUS_EV_REBATES_FLOW_DROPDOWN_NAME_VEHICLE_MANUFACTURER',
            )}
            options={vehicleManufacturers}
            state={form.values.vehicleManufacturer || ''}
            setState={e => {
              void form.setValue('vehicleManufacturer', e);
              void form.setValue('vehicleModel', '');
            }}
            errorMsg={(form.errors.vehicleManufacturer as string) || ''}
          />
        </Grid>
      </Box>
      
      {!!form.values.vehicleManufacturer &&
        vehicleModelOptions.length > 0 && (
          <Box mt={3}>
            <Grid item xs={12}>
              <DropdownMenu
                name={contentMCG.get(
                  'PGE_PLUS_EV_REBATES_FLOW_DROPDOWN_NAME_VEHICLE_MODEL',
                )}
                options={vehicleModelOptions}
                state={form.values.vehicleModel || ''}
                setState={e => {
                  void form.setValue('vehicleModel', e);
                }}
                errorMsg={(form.errors.vehicleModel as string) || ''}
              />
            </Grid>
          </Box>
        )}

      <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />

      <ProceedOrCancelButton
        back={backRoute()}
        backRouteState={
          existingEnrollments?.evPulse?.length
            ? { state: existingEnrollments.evPulse[0].vin }
            : existingChargerEnrollment
            ? {
                state:
                  existingChargerEnrollment.serialNo ??
                  existingChargerEnrollment.vin,
              }
            : undefined
        }
        backLabel={content.get('BACK')}
        disable={
          !form.values?.modelNumber ||
          form.values?.modelNumberImage === undefined ||
          !form.values?.vehicleManufacturer ||
          (!!form.values.vehicleManufacturer &&
            vehicleModelOptions.length > 0 &&
            !form.values.vehicleModel)
        }
      />

      <Box mt={3}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
      <Box>
        {!rebateInfo?.smartCharging?.modelNumberImage && (
          <WelcomeToPGEPlusModal />
        )}
      </Box>
    </form>
  );
};

export default UploadModelNumber;
